import { cloneElement, PropsWithChildren } from 'react';

import { Button, CheckOutline, forwardRef, HStack, rem, StyleProps, Text } from '@bonusly/design-system';

import { useHomepageRedesignEnabled } from '../../../../modules/bonus-feed/features/recognition/components/BonusFeed/hooks/useHomepageRedesignEnabled';
import { useActionInput } from '../../context';
import { useTagState } from './hooks/use-tag-state';
import { TagButtonIcon } from './tag-button-icon';
import { Tag } from './types';
import { buttonStyle } from './utils/button-style';

export interface TagButtonProps extends PropsWithChildren, StyleProps {
	tag: Tag;
}

export const TagButton = forwardRef<TagButtonProps, 'button'>(({ tag, ...rest }: TagButtonProps, ref) => {
	const { editor } = useActionInput();
	const { isActive, isEnabled, schema } = useTagState(tag);
	const { startDraft } = useActionInput();
	const isHomepageRedesignEnabled = useHomepageRedesignEnabled();

	if (!isEnabled) {
		return null;
	}

	const handleClick = () => {
		if (editor) {
			startDraft();
			editor.chain().insertContent(schema.trigger).focus().run();
		}
	};

	// Redesigned compact button style (matches visibility button but with border instead of bg)
	if (isHomepageRedesignEnabled) {
		// Dynamic colors based on active state - icon uses currentColor to change with hover
		const bgColor = isActive ? schema.activeBg : 'transparent';
		const textColor = isActive ? schema.activeText : 'text.default';
		const borderColor = isActive ? 'transparent' : 'border.default';

		return (
			<Button
				variant="ghost"
				colorScheme="secondary"
				size="sm"
				h={rem(24)}
				minW="auto"
				px={rem(6)}
				py={rem(2)}
				borderRadius={rem(8)}
				bg={bgColor}
				color={textColor}
				borderWidth="1px"
				borderColor={borderColor}
				_hover={{ bg: schema.activeBg, color: schema.activeText, borderColor: 'transparent' }}
				onClick={handleClick}
				ref={ref}
				{...rest}
			>
				{isActive ? <CheckOutline boxSize={4} color="currentColor" /> : cloneElement(schema.icon, { boxSize: 4, color: 'currentColor' })}
				<Text fontSize={rem(14)} whiteSpace="nowrap" textTransform="lowercase">
					{schema.label}
				</Text>
			</Button>
		);
	}

	return (
		<Button
			bg="secondary.bgAlpha"
			borderRadius={rem(22)}
			colorScheme="secondary"
			color="text.default"
			fontSize={{ base: 'sm', xs: 'md' }}
			h={{ base: rem(36), xs: rem(44) }}
			isActive={isActive}
			onClick={handleClick}
			pl={{ base: 4, xs: 2 }}
			ref={ref}
			variant="alpha"
			{...buttonStyle(schema)}
			{...rest}
		>
			<HStack spacing={{ base: 1, xs: 2 }}>
				<TagButtonIcon tag={tag} />
				<>{schema.label}</>
			</HStack>
		</Button>
	);
});

TagButton.displayName = 'TagButton';
